import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { Toaster as ShadcnToaster } from './components/ui/toaster';
import { AuthProvider, useAuth, UserRoles } from './context/AuthContext';
import { ConsentProvider } from './context/ConsentContext';
import TrackingLoader from './components/TrackingLoader';
import CookieBanner from './components/CookieBanner';
import CopilotMount from './components/CopilotMount';
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import SetupPasswordPage from './pages/SetupPasswordPage';
import MitarbeiterLoginPage from './pages/MitarbeiterLoginPage';
import MitarbeiterSetPasswordPage from './pages/MitarbeiterSetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import InhaberDashboard from './pages/InhaberDashboard';
import MitarbeiterDashboard from './pages/MitarbeiterDashboard';
import LegalPage from './pages/LegalPage';
import CoursesOverview from './pages/CoursesOverview';
import CoursePlayer from './pages/CoursePlayer';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminInsightsPage from './pages/AdminInsightsPage';
import SmartVoltProLandingPage from './pages/pro/SmartVoltProLandingPage';
import InhaberProDashboard from './pages/pro/InhaberProDashboard';
import MonteurProApp from './pages/pro/MonteurProApp';
import CertificateVerify from './pages/CertificateVerify';
import PartnerPage from './pages/PartnerPage';
import DigiStoreGuidePage from './pages/DigiStoreGuidePage';
import DankePage from './pages/DankePage';
import AdminOrdersPage from './pages/AdminOrdersPage';

// Protected Route Component for Company/Admin users
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, userType } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Laden...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // If employee is logged in but trying to access company route, redirect
  if (userType === 'mitarbeiter') {
    return <Navigate to="/elite-schmiede" />;
  }
  
  return children;
};

// Protected Route for Mitarbeiter
const MitarbeiterRoute = ({ children }) => {
  const { isAuthenticated, loading, userType } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Laden...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/mitarbeiter-login" />;
  }
  
  // If company user is logged in but trying to access mitarbeiter route, redirect
  if (userType === 'company') {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Smart Dashboard Router - redirects to the right dashboard based on role
const SmartDashboard = () => {
  const { isAuthenticated, loading, role, userType, company, employee, logout } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Laden...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Mitarbeiter - show Elite-Schmiede dashboard
  if (userType === 'mitarbeiter' || role === UserRoles.MITARBEITER) {
    return <MitarbeiterDashboard employee={employee} onLogout={logout} />;
  }
  
  // Admin - show full admin panel
  if (role === UserRoles.ADMIN) {
    return <Navigate to="/admin" />;
  }
  
  // Inhaber - show Chef-Cockpit
  if (role === UserRoles.INHABER) {
    return <InhaberDashboard company={company} onLogout={logout} />;
  }
  
  // Default fallback to old dashboard
  return <DashboardPage />;
};

// Elite-Schmiede Route (Mitarbeiter Dashboard)
const EliteSchmiede = () => {
  const { isAuthenticated, loading, employee, logout, userType } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Laden...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/mitarbeiter-login" />;
  }
  
  // If company user, redirect to dashboard
  if (userType === 'company') {
    return <Navigate to="/dashboard" />;
  }
  
  return <MitarbeiterDashboard employee={employee} onLogout={logout} />;
};

// Chef-Cockpit Route (Inhaber Dashboard)
const ChefCockpit = () => {
  const { isAuthenticated, loading, company, logout, role, userType } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Laden...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // If mitarbeiter, redirect to elite-schmiede
  if (userType === 'mitarbeiter') {
    return <Navigate to="/elite-schmiede" />;
  }
  
  // Admins can also view this
  return <InhaberDashboard company={company} onLogout={logout} />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Auth Routes */}
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      <Route path="/mitarbeiter-login" element={<MitarbeiterLoginPage />} />
      <Route path="/mitarbeiter-setup" element={<MitarbeiterSetPasswordPage />} />
      
      {/* Smart Dashboard - Redirects based on role */}
      <Route 
        path="/dashboard" 
        element={<SmartDashboard />} 
      />
      
      {/* Chef-Cockpit (Inhaber Dashboard) */}
      <Route 
        path="/chef-cockpit" 
        element={<ChefCockpit />} 
      />
      
      {/* Elite-Schmiede (Mitarbeiter Dashboard) */}
      <Route 
        path="/elite-schmiede" 
        element={<EliteSchmiede />} 
      />
      
      {/* Admin Panel */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Legal Pages */}
      <Route path="/legal/:pageType" element={<LegalPage />} />
      
      {/* Neues Kurs-System */}
      <Route path="/kurse" element={<CoursesOverview />} />
      <Route path="/kurs/:moduleId" element={<CoursePlayer />} />
      <Route path="/kurs/:moduleId/:lessonId" element={<CoursePlayer />} />
      
      {/* Admin Analytics */}
      <Route path="/admin-analytics" element={
        <ProtectedRoute><AdminAnalytics /></ProtectedRoute>
      } />

      {/* Admin: Live-Insights (Cockpit) */}
      <Route path="/admin/insights" element={
        <ProtectedRoute><AdminInsightsPage /></ProtectedRoute>
      } />

      {/* SmartVolt Pro – Upgrade-Landingpage (öffentlich für eingeloggte Chefs) */}
      <Route path="/pro" element={
        <ProtectedRoute><SmartVoltProLandingPage /></ProtectedRoute>
      } />
      <Route path="/pro/dashboard" element={
        <ProtectedRoute><InhaberProDashboard /></ProtectedRoute>
      } />
      <Route path="/pro/mobile" element={
        <ProtectedRoute><MonteurProApp /></ProtectedRoute>
      } />

      {/* Admin: Digistore24 Bestellungen-Monitor */}
      <Route path="/admin/orders" element={
        <ProtectedRoute><AdminOrdersPage /></ProtectedRoute>
      } />
      
      {/* Partnerschaft & Zertifikat-Verifizierung */}
      <Route path="/partner" element={<PartnerPage />} />
      <Route path="/zertifikat-pruefen" element={<CertificateVerify />} />
      <Route path="/zertifikat-pruefen/:certId" element={<CertificateVerify />} />
      
      {/* Digistore24 Setup-Anleitung */}
      <Route path="/digistore-anleitung" element={<DigiStoreGuidePage />} />
      
      {/* Danke-Seite nach Kauf (Google Ads Conversion) */}
      <Route path="/danke" element={<DankePage />} />
      <Route path="/probelektion" element={<Navigate to="/#pricing" replace />} />
      <Route path="/kostenlose-probelektion" element={<Navigate to="/#pricing" replace />} />
      <Route path="/schnupperkurs" element={<Navigate to="/#pricing" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ConsentProvider>
              <TrackingLoader />
              <AppRoutes />
              <CookieBanner />
              <CopilotMount />
              <Toaster />
              <ShadcnToaster />
            </ConsentProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
